import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "approveModal", "rejectModal",
    "approveModalName", "approveModalLoginId",
    "rejectModalName", "rejectModalLoginId",
    "rejectionReason",
    "batchApproveModal", "batchDeactivateModal",
    "batchApproveCount", "batchDeactivateCount"
  ]

  static values = {
    selectedFmId: String
  }

  // 승인 모달 열기
  openApproveModal(event) {
    const button = event.currentTarget
    const fmId = button.dataset.fmId
    const name = button.dataset.fmName
    const loginId = button.dataset.fmLoginId

    this.selectedFmIdValue = fmId
    this.approveModalNameTarget.textContent = name
    this.approveModalLoginIdTarget.textContent = '@' + loginId

    this.approveModalTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'

    // Lucide 아이콘 재렌더링
    if (typeof lucide !== 'undefined') {
      lucide.createIcons()
    }
  }

  // 승인 모달 닫기
  closeApproveModal() {
    this.approveModalTarget.classList.add('hidden')
    document.body.style.overflow = ''
    this.selectedFmIdValue = ''
  }

  // 승인 확인
  async confirmApprove() {
    // 승인 버튼 비활성화 (중복 클릭 방지)
    const modalContent = this.approveModalTarget.querySelector(':scope > [data-modal-target="content"]')
    const confirmButton = modalContent?.querySelector(':scope > [data-modal-target="footer"] [data-modal-target="confirmButton"]')
    if (confirmButton) {
      confirmButton.disabled = true
      confirmButton.textContent = '처리 중...'
    }

    try {
      const response = await fetch(`/workplace/organization/field_managers/${this.selectedFmIdValue}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const result = await response.json()

      if (result.success) {
        this.closeApproveModal()
        location.reload()
      } else {
        alert(result.error?.message || '승인 처리 중 오류가 발생했습니다.')
        // 버튼 복구
        if (confirmButton) {
          confirmButton.disabled = false
          confirmButton.textContent = '승인'
        }
      }
    } catch (error) {
      console.error('Approve error:', error)
      alert('승인 처리 중 오류가 발생했습니다.')
      // 버튼 복구
      if (confirmButton) {
        confirmButton.disabled = false
        confirmButton.textContent = '승인'
      }
    }
  }

  // 거절 모달 열기
  openRejectModal(event) {
    const button = event.currentTarget
    const fmId = button.dataset.fmId
    const name = button.dataset.fmName
    const loginId = button.dataset.fmLoginId

    this.selectedFmIdValue = fmId
    this.rejectModalNameTarget.textContent = name
    this.rejectModalLoginIdTarget.textContent = '@' + loginId
    this.rejectionReasonTarget.value = ''
    this.rejectModalTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'

    // Lucide 아이콘 재렌더링
    if (typeof lucide !== 'undefined') {
      lucide.createIcons()
    }
  }

  // 거절 모달 닫기
  closeRejectModal() {
    this.rejectModalTarget.classList.add('hidden')
    document.body.style.overflow = ''
    this.selectedFmIdValue = ''
  }

  // 거절 확인
  async confirmReject() {
    const reason = this.rejectionReasonTarget.value

    // 거절 버튼 비활성화 (중복 클릭 방지)
    const confirmButton = this.rejectModalTarget.querySelector('[data-modal-target="confirmButton"]')
    if (confirmButton) {
      confirmButton.disabled = true
      confirmButton.textContent = '처리 중...'
    }

    try {
      const response = await fetch(`/workplace/organization/field_managers/${this.selectedFmIdValue}/reject`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ reason: reason })
      })

      const result = await response.json()

      if (result.success) {
        this.closeRejectModal()
        location.reload()
      } else {
        alert(result.error?.message || '거절 처리 중 오류가 발생했습니다.')
        // 버튼 복구
        if (confirmButton) {
          confirmButton.disabled = false
          confirmButton.textContent = '거절'
        }
      }
    } catch (error) {
      console.error('Reject error:', error)
      alert('거절 처리 중 오류가 발생했습니다.')
      // 버튼 복구
      if (confirmButton) {
        confirmButton.disabled = false
        confirmButton.textContent = '거절'
      }
    }
  }

  // ESC 키로 모달 닫기
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeApproveModal()
      this.closeRejectModal()
      this.closeBatchApproveModal()
      this.closeBatchDeactivateModal()
    }
  }

  // 선택된 FM ID 가져오기 (checkbox-select 컨트롤러에서)
  getSelectedIds() {
    const checkboxSelectController = this.application.getControllerForElementAndIdentifier(
      this.element,
      "checkbox-select"
    )
    return checkboxSelectController ? checkboxSelectController.getSelectedIds() : []
  }

  // 일괄 승인 모달 열기
  openBatchApproveModal(event) {
    event.preventDefault()
    const selectedIds = this.getSelectedIds()
    if (selectedIds.length === 0) return

    if (this.hasBatchApproveCountTarget) {
      this.batchApproveCountTarget.textContent = selectedIds.length
    }

    if (this.hasBatchApproveModalTarget) {
      this.batchApproveModalTarget.classList.remove('hidden')
      document.body.style.overflow = 'hidden'
      if (typeof lucide !== 'undefined') {
        lucide.createIcons()
      }
    }
  }

  // 일괄 승인 모달 닫기
  closeBatchApproveModal() {
    if (this.hasBatchApproveModalTarget) {
      this.batchApproveModalTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }
  }

  // 일괄 승인 확인
  async confirmBatchApprove() {
    const selectedIds = this.getSelectedIds()
    if (selectedIds.length === 0) return

    const confirmButton = this.batchApproveModalTarget.querySelector('[data-modal-target="confirmButton"]')
    if (confirmButton) {
      confirmButton.disabled = true
      confirmButton.textContent = '처리 중...'
    }

    try {
      const response = await fetch('/workplace/organization/field_managers/batch_approve', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ selected_ids: selectedIds })
      })

      const result = await response.json()

      if (result.success) {
        this.closeBatchApproveModal()
        location.reload()
      } else {
        alert(result.error?.message || '일괄 승인 처리 중 오류가 발생했습니다.')
        if (confirmButton) {
          confirmButton.disabled = false
          confirmButton.textContent = '승인'
        }
      }
    } catch (error) {
      console.error('Batch approve error:', error)
      alert('일괄 승인 처리 중 오류가 발생했습니다.')
      if (confirmButton) {
        confirmButton.disabled = false
        confirmButton.textContent = '승인'
      }
    }
  }

  // 일괄 비활성화 모달 열기
  openBatchDeactivateModal(event) {
    event.preventDefault()
    const selectedIds = this.getSelectedIds()
    if (selectedIds.length === 0) return

    if (this.hasBatchDeactivateCountTarget) {
      this.batchDeactivateCountTarget.textContent = selectedIds.length
    }

    if (this.hasBatchDeactivateModalTarget) {
      this.batchDeactivateModalTarget.classList.remove('hidden')
      document.body.style.overflow = 'hidden'
      if (typeof lucide !== 'undefined') {
        lucide.createIcons()
      }
    }
  }

  // 일괄 비활성화 모달 닫기
  closeBatchDeactivateModal() {
    if (this.hasBatchDeactivateModalTarget) {
      this.batchDeactivateModalTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }
  }

  // 일괄 비활성화 확인
  async confirmBatchDeactivate() {
    const selectedIds = this.getSelectedIds()
    if (selectedIds.length === 0) return

    const confirmButton = this.batchDeactivateModalTarget.querySelector('[data-modal-target="confirmButton"]')
    if (confirmButton) {
      confirmButton.disabled = true
      confirmButton.textContent = '처리 중...'
    }

    try {
      const response = await fetch('/workplace/organization/field_managers/batch_deactivate', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ selected_ids: selectedIds })
      })

      const result = await response.json()

      if (result.success) {
        this.closeBatchDeactivateModal()
        location.reload()
      } else {
        alert(result.error?.message || '일괄 비활성화 처리 중 오류가 발생했습니다.')
        if (confirmButton) {
          confirmButton.disabled = false
          confirmButton.textContent = '비활성화'
        }
      }
    } catch (error) {
      console.error('Batch deactivate error:', error)
      alert('일괄 비활성화 처리 중 오류가 발생했습니다.')
      if (confirmButton) {
        confirmButton.disabled = false
        confirmButton.textContent = '비활성화'
      }
    }
  }

  connect() {
    // ESC 키 이벤트 바인딩
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }
}
